import { useState, useEffect, useCallback } from "react";
import { DesktopLayout } from "../../components/DesktopLayout";
import { supabase } from "../../lib/supabase";
import { formatDataHora, formatHora } from "../../data/mock";
import { RefreshCw, Footprints, QrCode, CheckCircle2, Clock, ChevronDown, ChevronRight } from "lucide-react";

const EMPRESA_ID = "ff438dc8-89fb-4d7c-9ea2-7e09162f242a";
const POLL_INTERVAL = 30000;

function agruparEmRondas(pontos) {
  const grupos = {};
  for (const p of pontos) {
    const dia   = (p.created_at || "").slice(0, 10);
    const chave = `${p.vigilante_id || p.vigilante_nome}__${dia}`;
    if (!grupos[chave]) {
      grupos[chave] = {
        id:            chave,
        vigilanteNome: p.vigilante_nome || "—",
        inicio:        p.created_at,
        fim:           p.created_at,
        pontos:        [],
      };
    }
    const g = grupos[chave];
    g.pontos.push(p);
    if (p.created_at < g.inicio) g.inicio = p.created_at;
    if (p.created_at > g.fim)    g.fim    = p.created_at;
  }
  return Object.values(grupos).sort((a, b) => (a.inicio < b.inicio ? 1 : -1));
}

function agruparPorDia(rondas) {
  const dias = {};
  for (const r of rondas) {
    const dia = r.inicio.slice(0, 10);
    if (!dias[dia]) dias[dia] = [];
    dias[dia].push(r);
  }
  return Object.entries(dias).sort(([a], [b]) => (a < b ? 1 : -1));
}

export default function RondasDesktop() {
  const [rondas, setRondas]             = useState([]);
  const [loading, setLoading]           = useState(true);
  const [atualizando, setAtualizando]   = useState(false);
  const [ultimaAtualizacao, setUltima]  = useState(null);
  const [expandido, setExpandido]       = useState({});

  const carregar = useCallback(async (mostrarSpinner = false) => {
    if (mostrarSpinner) setAtualizando(true);

    const { data, error } = await supabase
      .from("liberty_ronda_pontos")
      .select("id, empresa_id, ponto, created_at, validado_em, vigilante_id, vigilante_nome, observacao")
      .eq("empresa_id", EMPRESA_ID)
      .order("created_at", { ascending: false })
      .limit(500);

    if (error) {
      console.error("[Rondas] Supabase:", error.message);
    } else {
      setRondas(agruparEmRondas(data || []));
    }

    setLoading(false);
    setUltima(new Date());
    if (mostrarSpinner) setAtualizando(false);
  }, []);

  useEffect(() => {
    carregar();
    const id = setInterval(() => carregar(), POLL_INTERVAL);
    return () => clearInterval(id);
  }, [carregar]);

  const toggleExpandido = (id) => {
    setExpandido((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const diasAgrupados = agruparPorDia(rondas);
  const fmtDia = (iso) => {
    const d = new Date(iso + "T00:00:00");
    const hoje = new Date(); hoje.setHours(0,0,0,0);
    const diff = hoje.getTime() - d.getTime();
    if (diff < 86400000) return "Hoje";
    if (diff < 172800000) return "Ontem";
    return d.toLocaleDateString("pt-BR", { weekday: "long", day: "numeric", month: "long" });
  };

  return (
    <DesktopLayout
      title="Rondas"
      subtitle="Histórico completo de rondas realizadas"
      actions={
        <div className="flex items-center gap-3">
          <button
            onClick={() => carregar(true)}
            disabled={atualizando}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-[13px] font-medium transition disabled:opacity-40"
            style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.09)", color: "rgba(255,255,255,0.6)" }}
          >
            <RefreshCw className={`w-3.5 h-3.5 ${atualizando ? "animate-spin" : ""}`} />
            Atualizar
          </button>
          {ultimaAtualizacao && (
            <p className="text-[11px] font-mono hidden md:block" style={{ color: "rgba(255,255,255,0.25)" }}>
              {ultimaAtualizacao.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
            </p>
          )}
        </div>
      }
    >
      {/* Resumo */}
      <div
        className="rounded-2xl p-5 mb-6 flex items-center justify-between gap-4"
        style={{ background: "rgba(59,130,246,0.06)", border: "1px solid rgba(59,130,246,0.15)" }}
      >
        <div className="flex items-center gap-4">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: "rgba(59,130,246,0.12)", border: "1px solid rgba(59,130,246,0.2)" }}
          >
            <Footprints className="w-5 h-5" style={{ color: "#3B82F6" }} />
          </div>
          <div>
            <p className="text-[10px] font-bold tracking-[0.12em] uppercase mb-0.5" style={{ color: "rgba(147,197,253,0.5)" }}>
              Histórico de rondas
            </p>
            <p className="text-[16px] font-bold text-white">
              {loading ? "—" : `${rondas.length} ronda${rondas.length !== 1 ? "s" : ""} em ${diasAgrupados.length} dia${diasAgrupados.length !== 1 ? "s" : ""}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg" style={{ background: "rgba(16,185,129,0.08)", border: "1px solid rgba(16,185,129,0.15)" }}>
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
          <span className="text-[12px] font-medium" style={{ color: "#34d399" }}>ao vivo</span>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="space-y-4">
          {[1,2,3].map(i => (
            <div key={i} className="h-20 rounded-2xl animate-pulse" style={{ background: "rgba(255,255,255,0.04)" }} />
          ))}
        </div>
      )}

      {/* Vazio */}
      {!loading && rondas.length === 0 && (
        <div
          className="rounded-2xl p-10 text-center"
          style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)" }}
        >
          <QrCode className="w-10 h-10 mx-auto mb-3" style={{ color: "rgba(255,255,255,0.15)" }} />
          <p className="text-[14px] font-semibold" style={{ color: "rgba(255,255,255,0.4)" }}>Nenhuma ronda registrada.</p>
          <p className="text-[12px] mt-1" style={{ color: "rgba(255,255,255,0.2)" }}>Aguardando registros de QR Code dos vigilantes.</p>
        </div>
      )}

      {/* Rondas agrupadas por dia */}
      <div className="space-y-6">
        {diasAgrupados.map(([dia, rondasDoDia]) => (
          <div key={dia}>
            <div className="flex items-center gap-3 mb-3">
              <span
                className="text-[11px] font-bold px-3 py-1 rounded-full capitalize"
                style={{ background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.5)", border: "1px solid rgba(255,255,255,0.08)" }}
              >
                {fmtDia(dia)}
              </span>
              <div className="flex-1 h-px" style={{ background: "rgba(255,255,255,0.05)" }} />
              <span className="text-[11px]" style={{ color: "rgba(255,255,255,0.25)" }}>
                {rondasDoDia.length} ronda{rondasDoDia.length !== 1 ? "s" : ""}
              </span>
            </div>

            <div className="space-y-2.5">
              {rondasDoDia.map((r) => {
                const open = expandido[r.id];
                const iniciais = r.vigilanteNome.split(" ").map((n) => n[0]).slice(0, 2).join("");
                return (
                  <div
                    key={r.id}
                    className="rounded-2xl overflow-hidden"
                    style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)" }}
                  >
                    <button
                      type="button"
                      onClick={() => toggleExpandido(r.id)}
                      className="w-full flex items-center gap-4 p-4 text-left transition"
                      onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.03)")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                    >
                      {/* Avatar */}
                      <div
                        className="w-10 h-10 rounded-xl flex items-center justify-center text-[11px] font-bold flex-shrink-0"
                        style={{ background: "rgba(59,130,246,0.12)", color: "#3B82F6" }}
                      >
                        {iniciais}
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <p className="text-[14px] font-bold text-white/90">{r.vigilanteNome}</p>
                        <div className="flex items-center gap-3 mt-0.5">
                          <span className="flex items-center gap-1 text-[12px]" style={{ color: "rgba(255,255,255,0.4)" }}>
                            <Clock className="w-3.5 h-3.5" />
                            {formatHora(r.inicio)} — {r.fim && r.fim !== r.inicio ? formatHora(r.fim) : "em andamento"}
                          </span>
                        </div>
                      </div>

                      {/* Pontos */}
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg" style={{ background: "rgba(16,185,129,0.08)", border: "1px solid rgba(16,185,129,0.15)" }}>
                          <QrCode className="w-3.5 h-3.5" style={{ color: "#10B981" }} />
                          <span className="text-[13px] font-bold" style={{ color: "#34d399" }}>{r.pontos.length}</span>
                          <span className="text-[11px]" style={{ color: "rgba(52,211,153,0.6)" }}>pt</span>
                        </div>
                        <span
                          className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 transition"
                          style={{ background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.4)" }}
                        >
                          {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                        </span>
                      </div>
                    </button>

                    {open && (
                      <div
                        className="px-4 pb-4"
                        style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }}
                      >
                        <p className="text-[10px] font-bold tracking-[0.12em] uppercase mt-3 mb-2" style={{ color: "rgba(255,255,255,0.3)" }}>
                          Pontos confirmados
                        </p>
                        <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3">
                          {r.pontos.map((p, idx) => (
                            <div
                              key={idx}
                              className="flex items-center gap-2 px-3 py-2 rounded-xl text-[12px]"
                              style={{ background: "rgba(16,185,129,0.06)", border: "1px solid rgba(16,185,129,0.1)" }}
                            >
                              <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" style={{ color: "#10B981" }} />
                              <span className="truncate" style={{ color: "rgba(255,255,255,0.7)" }}>
                                {p.ponto || "Ponto"}
                              </span>
                              <span className="ml-auto font-mono text-[10px] flex-shrink-0" style={{ color: "rgba(255,255,255,0.25)" }}>
                                {formatHora(p.validado_em)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </DesktopLayout>
  );
}
